import { useEffect, useRef, useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { db, dbState } from '../db/db';
import { createClerkSupabaseClient } from '../utils/supabase';

/**
 * Yerel Dexie veritabanı ("TurarkYoutubePlannerDB") tek bir paylaşılan IndexedDB'dir —
 * kullanıcı bazında AYRI değildir. Bu anahtar, o veritabanındaki verinin şu an hangi
 * Clerk kullanıcısına ait olduğunu localStorage'da işaretler. Aynı tarayıcıda farklı
 * bir kullanıcı oturum açtığında bu etiket uyuşmazsa, önceki kullanıcının verisi
 * (yedeklenmiş olsun/olmasın) yeni kullanıcıya asla karışmasın diye yerel veri
 * tamamen temizlenir — restore/backup mantığı ondan sonra çalışır.
 */
const LOCAL_OWNER_KEY = 'bitmiyo_local_data_owner';

async function clearAllLocalTables() {
  await db.transaction('rw', db.tables, async () => {
    for (const table of db.tables) {
      await table.clear();
    }
  });
}

export default function SyncManager() {
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [isRestoring, setIsRestoring] = useState(false);
  const [isBanned, setIsBanned] = useState(localStorage.getItem('bitmiyo_user_banned') === 'true');
  const lastSyncTimeRef = useRef<number>(0);
  const supabaseClientRef = useRef<any>(null);
  const realtimeChannelRef = useRef<any>(null);

  useEffect(() => {
    if (!isSignedIn || !user) {
      // Çıkış yapıldı (veya henüz giriş yapılmadı): bu tarayıcıda kayıtlı olan kullanıcının
      // verisini son kez yedekle, YEDEKLEME BAŞARILI OLURSA yerel veriyi temizle. Çevrimdışıysa
      // (yedekleme başarısız olursa) veri kaybını önlemek için yerelde bırakılır — bir sonraki
      // girişte sahiplik kontrolü (aşağıda) yine de yanlış kullanıcıya karışmasını engeller.
      const outgoingUserId = localStorage.getItem(LOCAL_OWNER_KEY);
      const outgoingClient = supabaseClientRef.current;
      if (outgoingUserId && outgoingClient) {
        void (async () => {
          let backedUp = false;
          try {
            backedUp = await performBackup(outgoingClient, outgoingUserId);
          } catch (err) {
            console.error('❌ Çıkış öncesi son yedekleme başarısız:', err);
          }
          if (backedUp) {
            try {
              await clearAllLocalTables();
              localStorage.removeItem(LOCAL_OWNER_KEY);
              console.log('✅ Çıkış: veri yedeklendi ve tarayıcıdan temizlendi.');
            } catch (err) {
              console.error('❌ Çıkışta yerel veri temizleme başarısız:', err);
            }
          } else {
            console.warn('⚠️ Çıkışta yedekleme tamamlanamadı (çevrimdışı olabilir) — yerel veri kaybı olmasın diye temizlenmedi.');
          }
        })();
      }

      // Clean up subscriptions on sign out
      if (realtimeChannelRef.current) {
        realtimeChannelRef.current.unsubscribe();
        realtimeChannelRef.current = null;
      }
      supabaseClientRef.current = null;
      setIsRestoring(false);
      setIsBanned(false);
      localStorage.removeItem('bitmiyo_user_banned');
      return;
    }

    const userId = user.id;

    async function initializeSync() {
      if (!user) return;
      try {
        const token = await getToken({ template: 'supabase' });
        if (!token) return;

        const supabase = createClerkSupabaseClient(token);
        supabaseClientRef.current = supabase;

        // Sahiplik koruması: bu tarayıcıdaki yerel veri başka bir Clerk kullanıcısına
        // aitse (veya sahiplik hiç etiketlenmemişse ama veri varsa), yeni kullanıcıya
        // ASLA karışmasın diye önce tamamen temizle. performBackup/isDbEmpty mantığı
        // bundan sonra çalışır, böylece "yeni kullanıcının yedeği önceki kullanıcının
        // verisiyle oluşturulur" gibi bir sızıntı imkansız hale gelir.
        const storedOwnerId = localStorage.getItem(LOCAL_OWNER_KEY);
        if (storedOwnerId && storedOwnerId !== userId) {
          // Not: setIsRestoring burada bilerek tetiklenmiyor — aşağıdaki normal akış zaten
          // isDbEmpty=true'ya göre gerekirse restore ekranını kendi try/finally'siyle açıp kapatıyor.
          console.warn('🧹 Bu tarayıcıda başka bir kullanıcıya ait yerel veri bulundu, temizleniyor...');
          try {
            await clearAllLocalTables();
          } catch (err) {
            console.error('❌ Kullanıcı değişiminde yerel veri temizleme başarısız:', err);
          }
        }
        localStorage.setItem(LOCAL_OWNER_KEY, userId);

        // Sync user profile to Supabase
        try {
          const clerkRole = (user.publicMetadata?.role as string) || (user.unsafeMetadata?.role as string) || 'user';
          const profileData = {
            user_id: userId,
            email: user.primaryEmailAddress?.emailAddress || null,
            full_name: user.fullName || null,
            avatar_url: user.imageUrl || null,
            role: clerkRole,
          };
          const { data: profileRecord, error: profileError } = await supabase
            .from('profiles')
            .upsert(profileData, { onConflict: 'user_id' })
            .select('is_banned')
            .maybeSingle();

          if (profileError) {
            console.error('⚠️ Could not sync user profile:', profileError.message);
          } else if (profileRecord) {
            if (profileRecord.is_banned) {
              console.warn('⚠️ User is banned!');
              setIsBanned(true);
              localStorage.setItem('bitmiyo_user_banned', 'true');
              setIsRestoring(false);
              return; // Stop sync initialization
            } else {
              setIsBanned(false);
              localStorage.removeItem('bitmiyo_user_banned');
            }
          }
        } catch (profileErr) {
          console.error('⚠️ Error upserting user profile:', profileErr);
        }

        // 1. Initial Sync Check
        const subjectsCount = await db.subjects.count();
        const plansCount = await db.studyPlans.count();
        const isDbEmpty = subjectsCount === 0 && plansCount === 0;

        const { data: record, error } = await supabase
          .from('backups')
          .select('updated_at, data')
          .eq('user_id', userId)
          .maybeSingle();

        if (error) {
          console.error('⚠️ Could not fetch initial backup metadata:', error);
        }

        const localSettings = await db.settings.toCollection().first();
        const localBackupDate = localSettings?.lastBackupDate;

        if (record && record.updated_at) {
          const remoteTime = new Date(record.updated_at).getTime();
          const localTime = localBackupDate ? new Date(localBackupDate).getTime() : 0;

          if (remoteTime > localTime || !localBackupDate) {
            console.log('🔄 Initial Sync: Remote backup is newer. Restoring...');
            if (isDbEmpty) {
              setIsRestoring(true);
            }
            
            dbState.isRestoring = true;
            try {
              const data = record.data;
              await db.transaction('rw', db.tables, async () => {
                for (const tableName in data) {
                  if (db.tables.some(t => t.name === tableName)) {
                    await db.table(tableName).clear();
                    await db.table(tableName).bulkAdd(data[tableName]);
                  }
                }
              });

              if (localSettings?.id) {
                await db.settings.update(localSettings.id, { lastBackupDate: record.updated_at });
              } else {
                await db.settings.add({ lastBackupDate: record.updated_at, geminiApiKey: '' } as any);
              }
              console.log('✅ Initial Sync: Restore successful!');
              
              if (isDbEmpty) {
                window.location.reload();
                return;
              }
            } catch (err) {
              console.error('❌ Initial Sync: Restore failed:', err);
            } finally {
              dbState.isRestoring = false;
              setIsRestoring(false);
            }
          } else if (remoteTime < localTime) {
            console.log('🔄 Initial Sync: Local is newer. Uploading backup...');
            await performBackup(supabase, userId);
          } else {
            console.log('🔄 Initial Sync: Already in sync.');
          }
        } else {
          // No remote backup exists
          if (!isDbEmpty) {
            console.log('🔄 Initial Sync: No remote backup. Creating first backup...');
            await performBackup(supabase, userId);
          }
        }

        // 2. Setup Realtime Subscription
        const channel = supabase
          .channel(`backup-sync-${userId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'backups',
              filter: `user_id=eq.${userId}`,
            },
            async (payload: any) => {
              console.log('⚡ Realtime Update Received:', payload);
              const newRecord = payload.new;
              if (!newRecord || !newRecord.data) return;

              const settingsObj = await db.settings.toCollection().first();
              const currentLocalBackupDate = settingsObj?.lastBackupDate;

              const remoteUpdatedAt = newRecord.updated_at;
              const remoteTime = new Date(remoteUpdatedAt).getTime();
              const localTime = currentLocalBackupDate ? new Date(currentLocalBackupDate).getTime() : 0;

              if (remoteTime > localTime) {
                console.log('🔄 Realtime Sync: Pulling remote changes...');
                dbState.isRestoring = true;
                try {
                  const data = newRecord.data;
                  await db.transaction('rw', db.tables, async () => {
                    for (const tableName in data) {
                      if (db.tables.some(t => t.name === tableName)) {
                        await db.table(tableName).clear();
                        await db.table(tableName).bulkAdd(data[tableName]);
                      }
                    }
                  });

                  if (settingsObj?.id) {
                    await db.settings.update(settingsObj.id, { lastBackupDate: remoteUpdatedAt });
                  } else {
                    await db.settings.add({ lastBackupDate: remoteUpdatedAt, geminiApiKey: '' } as any);
                  }
                  console.log('✅ Realtime Sync: Applied remote backup successfully.');
                } catch (err) {
                  console.error('❌ Realtime Sync: Application failed:', err);
                } finally {
                  dbState.isRestoring = false;
                }
              }
            }
          )
          .subscribe();

        realtimeChannelRef.current = channel;
      } catch (err) {
        console.error('❌ SyncManager initialization failed:', err);
      }
    }

    initializeSync();

    // 3. Keep Realtime token refreshed (Clerk JWT expires in 60s)
    const tokenRefreshId = setInterval(async () => {
      try {
        const token = await getToken({ template: 'supabase' });
        if (token && supabaseClientRef.current) {
          supabaseClientRef.current.realtime.setAuth(token);
        }
      } catch (err) {
        console.error('❌ Token refresh for realtime failed:', err);
      }
    }, 45 * 1000);

    return () => {
      clearInterval(tokenRefreshId);
      if (realtimeChannelRef.current) {
        realtimeChannelRef.current.unsubscribe();
        realtimeChannelRef.current = null;
      }
    };
  }, [isSignedIn, user, getToken]);

  // 4. Listen to local writes via force-backup and online status
  useEffect(() => {
    const handleForceBackup = async () => {
      if (!isSignedIn || !user || !supabaseClientRef.current || dbState.isRestoring) return;
      
      // Cooldown to avoid request spamming (min 3 seconds between requests)
      if (Date.now() - lastSyncTimeRef.current < 3000) {
        return;
      }
      
      console.log('🔄 Triggering backup...');
      await performBackup(supabaseClientRef.current, user.id);
    };

    window.addEventListener('force-backup', handleForceBackup);
    window.addEventListener('online', handleForceBackup);

    return () => {
      window.removeEventListener('force-backup', handleForceBackup);
      window.removeEventListener('online', handleForceBackup);
    };
  }, [isSignedIn, user]);

  /** @returns yedekleme başarıyla tamamlandıysa (veya yedeklenecek veri yoksa) true; başarısızsa false. */
  const performBackup = async (supabase: any, targetUserId?: string): Promise<boolean> => {
    const userId = targetUserId ?? user?.id;
    if (!userId) return false;
    if (isBanned || localStorage.getItem('bitmiyo_user_banned') === 'true') {
      console.warn('Backup skipped: User is banned');
      return false;
    }
    try {
      const data: any = {};
      const tableNames = db.tables.map(table => table.name);

      let totalRecords = 0;
      for (const name of tableNames) {
        let tableData = await db.table(name).toArray();
        if (name === 'settings') {
          tableData = tableData.map((s: any) => {
            const { geminiApiKey, ...rest } = s;
            return rest;
          });
        }
        data[name] = tableData;
        totalRecords += tableData.length;
      }

      if (totalRecords === 0) return true; // Yedeklenecek veri yok — temizlemek güvenli.

      const { data: responseData, error } = await supabase.from('backups').upsert({
        user_id: userId,
        data: data,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_id' }).select('updated_at').single();

      if (error) {
        console.error('❌ Auto-backup write failed:', error);
        return false;
      }

      const serverUpdatedAt = responseData?.updated_at;
      if (serverUpdatedAt) {
        console.log('✅ Auto-backup successful. Server time:', serverUpdatedAt);
        lastSyncTimeRef.current = Date.now();

        const existing = await db.settings.toCollection().first();
        if (existing) {
          await db.settings.update(existing.id!, { lastBackupDate: serverUpdatedAt });
        } else {
          await db.settings.add({ lastBackupDate: serverUpdatedAt, geminiApiKey: '' } as any);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Auto-backup execution failed:', error);
      return false;
    }
  };

  if (isBanned) {
    return (
      <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col items-center justify-center text-white p-6 text-center select-none">
        <div className="w-20 h-20 rounded-3xl bg-danger-100 dark:bg-danger-500/10 border border-danger-500/20 flex items-center justify-center mb-6 text-danger-500 shadow-2xl shadow-danger-500/10">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        </div>
        <h2 className="text-3xl font-black text-white mb-2 tracking-tight">HESABINIZ YASAKLANDI</h2>
        <p className="text-slate-400 font-medium max-w-md leading-relaxed text-sm">
          Bitmiyo topluluk kurallarını ihlal ettiğiniz için hesabınız kalıcı olarak yasaklanmıştır. Paylaşılan tüm planlarınız yayından kaldırılmıştır.
        </p>
        <button
          onClick={() => window.location.href = 'mailto:destek@bitmiyo.com'}
          className="mt-8 px-6 py-3 bg-danger-500 hover:bg-danger-600 text-white font-bold rounded-xl active:scale-95 transition-all text-xs uppercase tracking-wider"
        >
          Destek Talebi Oluştur
        </button>
      </div>
    );
  }

  if (!isRestoring) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-md flex flex-col items-center justify-center text-white">
       <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-6 shadow-lg shadow-blue-500/50"></div>
       <h2 className="text-2xl font-bold mb-2">Buluttan verileriniz alınıyor...</h2>
       <p className="text-slate-300 font-medium animate-pulse">Lütfen bekleyin, çalışma planlarınız yükleniyor.</p>
    </div>
  );
}
